
import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  FileText,
  Upload,
  Download,
  Search,
  Filter,
  Plus,
  Eye,
  Trash2,
  FolderOpen,
  Calendar,
  User,
  Building,
  Grid3X3,
  List,
  MoreVertical,
  Edit,
  FileStack,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle
} from "lucide-react";

// Données fictives pour les documents du registre du personnel (déposés par l'agence)
const registrePersonnelDocs = [
  {
    id: 1,
    name: "Registre du personnel - Janvier 2024",
    type: "PDF",
    size: "2.4 MB",
    uploadDate: "2024-01-31",
    uploadedBy: "JM Interim",
    downloads: 12,
    description: "Registre mensuel du personnel intérimaire"
  },
  {
    id: 2,
    name: "Registre du personnel - Février 2024",
    type: "PDF",
    size: "2.1 MB",
    uploadDate: "2024-02-29",
    uploadedBy: "JM Interim",
    downloads: 8,
    description: "Registre mensuel du personnel intérimaire"
  },
  {
    id: 3,
    name: "Registre du personnel - Mars 2024",
    type: "PDF",
    size: "2.8 MB",
    uploadDate: "2024-03-31",
    uploadedBy: "JM Interim",
    downloads: 15,
    description: "Registre mensuel du personnel intérimaire"
  }
];

// Données fictives pour les documents travailleurs handicapés (déposés par l'agence)
const travailleurHandicapeDocs = [
  {
    id: 4,
    name: "Déclaration DOETH 2023",
    type: "PDF",
    size: "1.8 MB",
    uploadDate: "2024-01-15",
    uploadedBy: "JM Interim",
    downloads: 25,
    description: "Déclaration d'obligation d'emploi des travailleurs handicapés"
  },
  {
    id: 5,
    name: "Liste des intérimaires RQTH",
    type: "PDF",
    size: "0.9 MB",
    uploadDate: "2024-02-10",
    uploadedBy: "JM Interim",
    downloads: 18,
    description: "Liste des intérimaires avec reconnaissance RQTH"
  },
  {
    id: 6,
    name: "Rapport annuel handicap 2023",
    type: "PDF",
    size: "3.2 MB",
    uploadDate: "2024-01-20",
    uploadedBy: "JM Interim",
    downloads: 22,
    description: "Rapport annuel sur l'emploi des travailleurs handicapés"
  }
];

// Données fictives pour les documents clients (déposés par les clients)
const mesDocuments = [
  {
    id: 7,
    name: "Contrat de prestation - Entreprise A",
    category: "Contrats",
    type: "PDF",
    size: "1.5 MB",
    uploadDate: "2024-03-10",
    uploadedBy: "Client Entreprise A",
    downloads: 5,
    status: "active",
    description: "Contrat de prestation de services intérim"
  },
  {
    id: 8,
    name: "Fiche de poste - Technicien",
    category: "Fiches de poste",
    type: "PDF",
    size: "0.8 MB",
    uploadDate: "2024-03-08",
    uploadedBy: "Client Entreprise B",
    downloads: 3,
    status: "active",
    description: "Description détaillée du poste de technicien"
  },
  {
    id: 9,
    name: "Plan de prévention",
    category: "Sécurité",
    type: "PDF",
    size: "2.1 MB",
    uploadDate: "2024-03-05",
    uploadedBy: "Client Entreprise C",
    downloads: 7,
    status: "active",
    description: "Plan de prévention des risques sur site"
  }
];

// Catégories pour les documents clients
const categoriesClients = [
  "Toutes",
  "Contrats",
  "Fiches de poste",
  "Sécurité",
  "Formation",
  "Juridique",
  "Administratif",
  "Autre",
];

export default function Documents() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Toutes");
  const [activeTab, setActiveTab] = useState("registre"); // registre, handicape, mes-documents
  const [viewMode, setViewMode] = useState('grid');
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadFormData, setUploadFormData] = useState({
    name: "",
    category: "",
    description: "",
    file: null
  });
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null);





  // Statistiques globales
  const [stats, setStats] = useState({
    registreTotal: 0,
    handicapeTotal: 0,
    mesDocsTotal: 0,
    totalDownloads: 0
  });

  // Fonction pour calculer les statistiques
  const calculateStats = () => {
    const registreTotal = registrePersonnelDocs.length;
    const handicapeTotal = travailleurHandicapeDocs.length;
    const mesDocsTotal = mesDocuments.length;
    const totalDownloads = [
      ...registrePersonnelDocs,
      ...travailleurHandicapeDocs,
      ...mesDocuments
    ].reduce((sum, doc) => sum + doc.downloads, 0);

    setStats({ registreTotal, handicapeTotal, mesDocsTotal, totalDownloads });
  };

  // Calculer les stats au chargement
  React.useEffect(() => {
    calculateStats();
  }, []);

  // Obtenir les documents selon l'onglet actif
  const getCurrentDocuments = () => {
    switch (activeTab) {
      case "registre":
        return registrePersonnelDocs;
      case "handicape":
        return travailleurHandicapeDocs;
      case "mes-documents":
        return mesDocuments;
      default:
        return [];
    }
  };

  // Filtrer les documents selon la recherche et la catégorie
  const filteredDocuments = getCurrentDocuments().filter(
    (doc) =>
      (doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.uploadedBy.toLowerCase().includes(searchTerm.toLowerCase())) &&
      (selectedCategory === "Toutes" || doc.category === selectedCategory)
  );

  // Fonction pour télécharger un document
  const handleDownload = (document) => {
    // Simuler le téléchargement et incrémenter le compteur
    toast.success(`Téléchargement de "${document.name}" démarré`);
    console.log("Téléchargement du document:", document);
  };

  // Fonction pour visualiser un document
  const handleView = (document) => {
    toast.info(`Ouverture de "${document.name}"`);
    console.log("Visualisation du document:", document);
  };

  // Fonction pour supprimer un document (seulement pour "Mes documents")
  const handleDelete = (documentId) => {
    if (activeTab === "mes-documents") {
      // Ici on supprimerait le document de la liste des documents clients
      toast.success("Document supprimé avec succès");
      console.log("Suppression du document:", documentId);
    }
  };

  // Fonction pour archiver/désarchiver un document (seulement pour "Mes documents")
  const toggleDocumentStatus = (documentId) => {
    if (activeTab === "mes-documents") {
      // Ici on changerait le statut du document
      toast.success("Statut du document modifié");
      console.log("Changement de statut du document:", documentId);
    }
  };

  // Fonction pour gérer l'upload de fichier (seulement pour "Mes documents")
  const handleFileUpload = async () => {
    if (!uploadFormData.name || !uploadFormData.category || !uploadFormData.file) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setIsLoading(true);
    try {
      // Simuler l'upload
      await new Promise(resolve => setTimeout(resolve, 2000));

      const newDocument = {
        id: Math.max(...mesDocuments.map(d => d.id), 0) + 1,
        name: uploadFormData.name,
        category: uploadFormData.category,
        description: uploadFormData.description,
        type: uploadFormData.file.type.includes('pdf') ? 'PDF' : 'DOC',
        size: `${(uploadFormData.file.size / (1024 * 1024)).toFixed(1)} MB`,
        uploadDate: new Date().toISOString().split('T')[0],
        uploadedBy: "Vous",
        downloads: 0,
        status: "active"
      };

      // Ici on ajouterait le document à la liste des documents clients
      setIsUploadDialogOpen(false);
      setUploadFormData({ name: "", category: "", description: "", file: null });
      toast.success("Document uploadé avec succès !");
      console.log("Nouveau document uploadé:", newDocument);
    } catch (error) {
      toast.error("Erreur lors de l'upload du document");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadFormData(prev => ({ ...prev, file }));
    }
  };

  return (
    <div className="w-full min-h-screen">
      <div className="w-full p-8">
        {/* Header Section */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <FileText className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-slate-900">
                  Gestion des Documents
                </h1>
                <p className="text-lg text-slate-600">
                  Organisez et partagez vos documents d'entreprise
                </p>
              </div>
            </div>

            {activeTab === "mes-documents" && (
              <motion.button
                onClick={() => setIsUploadDialogOpen(true)}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl flex items-center space-x-2"
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <Upload className="w-5 h-5" />
                <span>Nouveau document</span>
              </motion.button>
            )}
          </div>
        </motion.div>

        {/* Statistiques */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
            whileHover={{ y: -2 }}
          >
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <User className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Registre du personnel</p>
                <p className="text-3xl font-bold text-slate-900">{stats.registreTotal}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
            whileHover={{ y: -2 }}
          >
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Travailleurs handicapés</p>
                <p className="text-3xl font-bold text-green-600">{stats.handicapeTotal}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
            whileHover={{ y: -2 }}
          >
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-orange-100 rounded-lg">
                <FolderOpen className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Mes documents</p>
                <p className="text-3xl font-bold text-orange-600">{stats.mesDocsTotal}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
            whileHover={{ y: -2 }}
          >
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Download className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Téléchargements totaux</p>
                <p className="text-3xl font-bold text-purple-600">{stats.totalDownloads}</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
        {/* Filtres et recherche */}
        <motion.div
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Rechercher des documents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
                />
              </div>

              {activeTab === "mes-documents" && (
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-slate-400" />
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {categoriesClients.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Onglets pour les trois sections */}
              <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
                <button
                  onClick={() => setActiveTab('registre')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    activeTab === 'registre'
                      ? "bg-white text-blue-700 shadow-sm"
                      : "text-slate-600 hover:text-slate-800"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span>Registre du personnel</span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('handicape')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    activeTab === 'handicape'
                      ? "bg-white text-blue-700 shadow-sm"
                      : "text-slate-600 hover:text-slate-800"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Travailleurs handicapés</span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('mes-documents')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    activeTab === 'mes-documents'
                      ? "bg-white text-blue-700 shadow-sm"
                      : "text-slate-600 hover:text-slate-800"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <FolderOpen className="w-4 h-4" />
                    <span>Mes documents</span>
                  </div>
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg font-medium transition-all duration-200 ${
                  viewMode === 'grid'
                    ? "bg-blue-100 text-blue-700 border border-blue-200"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg font-medium transition-all duration-200 ${
                  viewMode === 'list'
                    ? "bg-blue-100 text-blue-700 border border-blue-200"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>
        {/* Liste des documents */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <DocumentsTable
            documents={filteredDocuments}
            onDownload={handleDownload}
            onView={handleView}
            onDelete={handleDelete}
            toggleStatus={toggleDocumentStatus}
            viewMode={viewMode}
            activeTab={activeTab}
          />
        </motion.div>
      </div>
      {/* Dialog d'upload */}
      {isUploadDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-900">Nouveau document</h3>
              <button
                onClick={() => setIsUploadDialogOpen(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Nom du document *
                </label>
                <input
                  type="text"
                  value={uploadFormData.name}
                  onChange={(e) => setUploadFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nom du document"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Catégorie *
                </label>
                <select
                  value={uploadFormData.category}
                  onChange={(e) => setUploadFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Sélectionner une catégorie</option>
                  {categoriesClients.slice(1).map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Description
                </label>
                <textarea
                  value={uploadFormData.description}
                  onChange={(e) => setUploadFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                  placeholder="Description du document"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Fichier *
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  accept=".pdf,.doc,.docx"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setIsUploadDialogOpen(false)}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium"
                disabled={isLoading}
              >
                Annuler
              </button>
              <button
                onClick={handleFileUpload}
                disabled={isLoading}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Upload..." : "Uploader"}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

// Composant DocumentsTable
const DocumentsTable = ({ documents, onDownload, onView, onDelete, toggleStatus, viewMode, activeTab }) => {
  if (viewMode === 'grid') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {documents.map((document) => (
          <motion.div
            key={document.id}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow"
            whileHover={{ y: -2 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 text-sm">{document.name}</h3>
                  <p className="text-xs text-slate-500">{document.type} • {document.size}</p>
                </div>
              </div>

              {/* Toggle de statut seulement pour "Mes documents" */}
              {activeTab === 'mes-documents' && document.status && (
                <div className="flex items-center space-x-2">
                  <span className={`text-xs font-medium ${document.status === 'active' ? 'text-green-700' : 'text-orange-700'}`}>
                    {document.status === 'active' ? 'ACTIF' : 'ARCHIVÉ'}
                  </span>
                  <button
                    onClick={() => toggleStatus(document.id)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      document.status === 'active' ? 'bg-green-500' : 'bg-orange-400'
                    }`}
                    role="switch"
                    aria-checked={document.status === 'active'}
                    title={document.status === 'active' ? 'Archiver le document' : 'Activer le document'}
                  >
                    <span
                      className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                        document.status === 'active' ? 'translate-x-5' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              )}
            </div>

            <div className="mb-4">
              <p className="text-sm text-slate-600 mb-2">{document.description}</p>
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>Par {document.uploadedBy}</span>
                <span>{document.uploadDate}</span>
              </div>
              <div className="flex items-center justify-between text-xs text-slate-500 mt-1">
                <span className="inline-flex items-center">
                  <Download className="w-3 h-3 mr-1" />
                  {document.downloads} téléchargements
                </span>
                {document.category && (
                  <span className="px-2 py-1 bg-slate-100 rounded text-xs">{document.category}</span>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => onDownload(document)}
                  className="flex items-center space-x-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
                >
                  <Download className="w-4 h-4" />
                  <span>Télécharger</span>
                </button>

                <button
                  onClick={() => onView(document)}
                  className="flex items-center space-x-1 px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm"
                >
                  <Eye className="w-4 h-4" />
                  <span>Voir</span>
                </button>
              </div>

              {/* Bouton supprimer seulement pour "Mes documents" */}
              {activeTab === 'mes-documents' && (
                <button
                  onClick={() => onDelete(document.id)}
                  className="p-1.5 text-slate-400 hover:text-red-600 transition-colors"
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    );
  }

  // Vue liste
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Document</th>
              {activeTab === 'mes-documents' && (
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Catégorie</th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Uploadé par</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Téléchargements</th>
              {activeTab === 'mes-documents' && (
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Statut</th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {documents.map((document) => (
              <motion.tr
                key={document.id}
                className="hover:bg-slate-50 transition-colors"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-100 rounded-lg mr-3">
                      <FileText className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-900">{document.name}</div>
                      <div className="text-sm text-slate-500">{document.type} • {document.size}</div>
                    </div>
                  </div>
                </td>
                {activeTab === 'mes-documents' && (
                  <td className="px-6 py-4 whitespace-nowrap">
                    {document.category && (
                      <span className="px-2 py-1 bg-slate-100 rounded text-xs text-slate-600">{document.category}</span>
                    )}
                  </td>
                )}
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">{document.uploadedBy}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{document.uploadDate}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                  <div className="flex items-center">
                    <Download className="w-3 h-3 mr-1" />
                    {document.downloads}
                  </div>
                </td>
                {activeTab === 'mes-documents' && document.status && (
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <span className={`text-xs font-medium ${document.status === 'active' ? 'text-green-700' : 'text-orange-700'}`}>
                        {document.status === 'active' ? 'ACTIF' : 'ARCHIVÉ'}
                      </span>
                      <button
                        onClick={() => toggleStatus(document.id)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          document.status === 'active' ? 'bg-green-500' : 'bg-orange-400'
                        }`}
                        role="switch"
                        aria-checked={document.status === 'active'}
                        title={document.status === 'active' ? 'Archiver le document' : 'Activer le document'}
                      >
                        <span
                          className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                            document.status === 'active' ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </td>
                )}
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => onDownload(document)}
                      className="text-blue-600 hover:text-blue-900 transition-colors"
                      title="Télécharger"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onView(document)}
                      className="text-slate-400 hover:text-blue-600 transition-colors"
                      title="Voir"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    {activeTab === 'mes-documents' && (
                      <button
                        onClick={() => onDelete(document.id)}
                        className="text-slate-400 hover:text-red-600 transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
